import { History, Location, LocationDescriptor } from 'history'
import React, { MutableRefObject, useEffect } from 'react'
import { Link, LinkProps } from 'react-router-dom'

import createDebug from './createDebug'

const debug = createDebug('scrollBehavior')

interface ScrollState {
  elementId: string
  elementClientRectTop: number
  pageYOffset: number
  innerWidth: number
  scrollToSelector?: string
}

export interface NavigateOpts {
  pathname: string
  search?: string
  scrollToSelector?: string
}

export function preserveScrollAndNavigate(
  history: History,
  navigateOpts: NavigateOpts,
  elRef: MutableRefObject<HTMLElement>
) {
  const { search, pathname, scrollToSelector } = navigateOpts
  const el = elRef.current
  const scrollState: ScrollState = {
    elementId: el && el.id,
    elementClientRectTop: el && el.getBoundingClientRect().top,
    pageYOffset: window.pageYOffset,
    innerWidth: window.innerWidth
  }

  debug(`storing scroll state for ${scrollState.elementId} before navigating to ${pathname}`, {
    scrollState,
    location: history.location
  })
  history.replace({ ...history.location, state: { ...history.location.state as Record<string, unknown>, ...scrollState } })

  debug(`navigating to: ${pathname}`)
  history.push({
    pathname,
    search,
    state: {
      scrollToSelector,
      ...scrollState,
      search: window.location.search
    }
  })
}

export function LinkToScrollPreservingRoute(props: LinkProps) {
  const { to, ...otherProps } = props

  return (
    <Link
      to={(location: Location) => {
        const toObjectOrString: LocationDescriptor = typeof to === 'function' ? to(location) : to

        return {
          ...(typeof toObjectOrString === 'string' ? { to: toObjectOrString } : toObjectOrString),
          search: location.state
            ? (location.state as { search: string }).search
            : typeof toObjectOrString === 'object'
              ? toObjectOrString.search
              : undefined,
          state: location.state
        }
      }}
      {...otherProps}
    />
  )
}

function restoreScrollYOffset(scrollState: ScrollState): void {
  const {
    elementId,
    elementClientRectTop: prevElementClientRectTop,
    pageYOffset: prevPageYOffset,
    innerWidth: prevInnerWidth,
    scrollToSelector
  } = scrollState

  if (scrollToSelector) {
    const element = document.querySelector(scrollToSelector)
    if (element) {
      debug(`scrollToSelector set; scrolling "${scrollToSelector}" into view`, scrollState)
      element.scrollIntoView()

      return
    }
  }

  if (!!prevPageYOffset && prevInnerWidth === window.innerWidth) {
    debug('window width unchanged; restoring page\'s previous scroll position', scrollState)
    window.scrollTo(window.pageXOffset, prevPageYOffset)

    return
  }

  const element = document.querySelector(`#${elementId}`)
  if (element) {
    debug('window width changed; restoring element\'s previous scroll offset', scrollState)
    const newElementClientRectTop = element.getBoundingClientRect().top
    window.scrollTo(window.pageXOffset, Math.max(0, newElementClientRectTop - prevElementClientRectTop))
  }
}

function repairHistory(history: History) {
  // clear the state tied to this history entry in case the user navigates
  // to a non-scroll-preserving route and later hits the back button
  debug('clearing history state')
  history.replace({ ...history.location, state: undefined })
}

export function useRestoreScroll(history: History, location: Location) {
  useEffect(() => {
    if (location.state) {
      restoreScrollYOffset(location.state as ScrollState)
      repairHistory(history)
    }
  }, [history, location.state])
}
